import { useState, useCallback } from 'react';
import { ChatError } from '../../types/message';
import { ChatFileUploadState } from './types';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/pdf',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

/**
 * チャットファイルアップロード機能を担当するフック
 */
export const useChatFileUpload = () => {
  const [state, setState] = useState<ChatFileUploadState>({
    uploadProgress: {},
    isUploading: false,
    error: null,
  });

  const validateFiles = useCallback((files: File[]): { valid: File[]; errors: string[] } => {
    const valid: File[] = [];
    const errors: string[] = [];

    files.forEach(file => {
      // ファイルサイズチェック
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: ファイルサイズが大きすぎます (最大10MB)`);
        return;
      }

      // ファイル形式チェック
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        errors.push(`${file.name}: サポートされていないファイル形式です`);
        return;
      }

      valid.push(file);
    });

    return { valid, errors };
  }, []);

  const uploadFiles = useCallback(async (files: File[]): Promise<{
    uploadedFiles: Array<{ file: File; url: string; id: string }>;
    errors: string[];
  }> => {
    setState(prev => ({ ...prev, isUploading: true, error: null }));

    const { valid, errors } = validateFiles(files);
    
    if (valid.length === 0) {
      setState(prev => ({
        ...prev,
        isUploading: false,
        error: {
          type: 'validation',
          message: 'アップロード可能なファイルがありません',
          details: errors,
        },
      }));
      return { uploadedFiles: [], errors };
    }

    const uploadedFiles: Array<{ file: File; url: string; id: string }> = [];

    try {
      // 各ファイルを並列でアップロード
      const uploadPromises = valid.map(async (file, index) => {
        const fileId = `${Date.now()}_${index}`;
        
        // 進捗状況を初期化
        setState(prev => ({
          ...prev,
          uploadProgress: {
            ...prev.uploadProgress,
            [fileId]: 0,
          },
        }));

        // TODO: 実際のファイルアップロードAPI呼び出し
        // 現在はモック実装
        return await mockFileUpload(file, fileId);
      });

      const results = await Promise.allSettled(uploadPromises);
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          uploadedFiles.push(result.value);
        } else {
          errors.push(`${valid[index].name}: アップロードに失敗しました`);
        }
      });

      setState(prev => ({
        ...prev,
        isUploading: false,
        uploadProgress: {}, // アップロード完了後にクリア
      }));

      return { uploadedFiles, errors };
    } catch (error) {
      setState(prev => ({
        ...prev,
        isUploading: false,
        uploadProgress: {},
        error: {
          type: 'network',
          message: 'ファイルアップロードに失敗しました',
          details: error,
        },
      }));

      return { uploadedFiles: [], errors: ['ファイルアップロードエラーが発生しました'] };
    }
  }, [validateFiles]);

  const mockFileUpload = async (file: File, fileId: string): Promise<{ file: File; url: string; id: string }> => {
    // アップロード進捗をシミュレート
    for (let progress = 0; progress <= 100; progress += 10) {
      setState(prev => ({
        ...prev,
        uploadProgress: {
          ...prev.uploadProgress,
          [fileId]: progress,
        },
      }));
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // モックURL生成
    const mockUrl = URL.createObjectURL(file);
    
    return {
      file,
      url: mockUrl,
      id: fileId,
    };
  };

  const cancelUpload = useCallback((fileId: string) => {
    setState(prev => {
      const newProgress = { ...prev.uploadProgress };
      delete newProgress[fileId];
      return {
        ...prev,
        uploadProgress: newProgress,
      };
    });
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    uploadFiles,
    cancelUpload,
    clearError,
    validateFiles,
  };
};